import { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { 
  TrendingUp, 
  AlertCircle, 
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { useApi } from '../../hooks/useApi';

const STATUS_CONFIG = {
  building_momentum: {
    icon: TrendingUp,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    badgeClass: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  },
  needs_attention: {
    icon: AlertCircle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200'
  },
  at_risk: {
    icon: AlertTriangle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    badgeClass: 'bg-red-100 text-red-700 border-red-200'
  }
};

export default function OpportunityMomentum({ opportunityId, compact = false }) {
  const [momentum, setMomentum] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const api = useApi();

  const fetchMomentum = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/sales-intelligence/momentum/${opportunityId}`);
      setMomentum(data);
    } catch (error) {
      console.error('Failed to fetch momentum:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (opportunityId) {
      fetchMomentum();
    }
  }, [opportunityId]);

  if (loading && !momentum) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin" />
        <span>Analyzing momentum...</span>
      </div>
    );
  }

  if (!momentum) return null;

  const config = STATUS_CONFIG[momentum.status] || STATUS_CONFIG.needs_attention;
  const Icon = config.icon;

  if (compact) {
    return (
      <Badge 
        variant="outline" 
        className={config.badgeClass}
        data-testid="momentum-badge"
      >
        <Icon className="h-3 w-3 mr-1" />
        {momentum.status_display}
      </Badge>
    );
  }

  return (
    <div 
      className={`rounded-lg border ${config.borderColor} ${config.bgColor} p-4`}
      data-testid="opportunity-momentum"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${config.bgColor}`}>
            <Icon className={`h-5 w-5 ${config.color}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className={`font-medium ${config.color}`}>
                {momentum.status_display}
              </span>
              <span className="text-xs text-muted-foreground">
                Score: {momentum.score}/100
              </span>
            </div>
            <p className="text-sm text-[#1A1A1A]/70 mt-0.5">
              {momentum.suggested_action}
            </p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="h-8 w-8 p-0"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {expanded && momentum.factors?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-current/10">
          <p className="text-xs font-medium mb-2 text-[#1A1A1A]/60">Contributing Factors:</p>
          <ul className="space-y-1">
            {momentum.factors.map((factor, i) => (
              <li key={i} className="text-xs text-[#1A1A1A]/70 flex items-start gap-2">
                <span className={config.color}>•</span>
                {factor}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Bulk momentum display for pipeline view
export function MomentumBadge({ status, statusDisplay }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.needs_attention;
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.badgeClass} text-xs`}>
      <Icon className="h-3 w-3 mr-1" />
      {statusDisplay}
    </Badge>
  );
}
